import type { FavoriteDto } from '@lista/shared-validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';

export function useFavorites() {
  const { services, locale } = useCore();
  return useQuery({ queryKey: [...queryKeys.favorites, locale], queryFn: services.favorites.all });
}

/** catalogItemId set - for star toggles anywhere in the app. */
export function useFavoriteIds(): Set<string> {
  const favorites = useFavorites();
  return new Set(favorites.data?.map((f) => f.catalogItemId));
}

export interface ToggleFavoriteVars {
  itemId: string;
  name: string;
  favorite: boolean;
}

/** Optimistic star toggle. UI feedback (toasts, Undo) is up to each app via mutate callbacks. */
export function useToggleFavorite() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, favorite }: ToggleFavoriteVars) =>
      favorite
        ? services.favorites.add(itemId).then(() => undefined)
        : services.favorites.remove(itemId),
    onMutate: async ({ itemId, name, favorite }) => {
      await qc.cancelQueries({ queryKey: queryKeys.favorites });
      const snapshot = qc.getQueriesData<FavoriteDto[]>({ queryKey: queryKeys.favorites });
      qc.setQueriesData<FavoriteDto[]>({ queryKey: queryKeys.favorites }, (list = []) =>
        favorite
          ? [
              ...list,
              {
                id: `optimistic-${itemId}`,
                catalogItemId: itemId,
                name,
                isCustom: false,
                categoryId: '',
                defaultUnit: 'PIECE',
                isActive: true,
                createdAt: new Date().toISOString(),
              },
            ]
          : list.filter((f) => f.catalogItemId !== itemId),
      );
      return { snapshot };
    },
    onError: (_err, _v, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.favorites });
      void qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
}
